import {
  ChevronDown,
  ClipboardPen,
  Copy,
  FilePlus2,
  Mail,
  Pencil,
  Plus,
  Sparkles,
  UserRound,
} from "lucide-react";
import type { JSX } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import { ValidationSuscriptionButton } from "@/common/enums/enums";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FormCompletionSheet } from "@/components/ui/forms/FormCompletionSheet";
import {
  SubscriptionOnlyButton,
  useSubscriptionValidation,
} from "@/components/ui/SubscriptionOnlyButton/SubscriptionOnlyButton";
import { v3ButtonVariants } from "@/components/ui/v3/button";
import { V3_MENU_CONTENT, V3_MENU_ITEM } from "@/components/ui/v3/menu";
import { NewNoteSidebar } from "@/components/ui/visits/NewNoteSidebar";
import type { ReadingLevel } from "@/lib/reading-level";
import type { SummaryLanguage } from "@/lib/summary-language";
import { cn } from "@/lib/utils";
import type { FileData } from "@/services/files";
import type { AssistantSurface } from "./layout";
import { NoteLanguageCaption, NoteLanguageMenu } from "./NoteLanguageMenu";
import { ReadingLevelDialog } from "./ReadingLevelDialog";

const NOTE_VALIDATIONS = [ValidationSuscriptionButton.RECORDING_USAGES];
const PLAN_VALIDATIONS = [ValidationSuscriptionButton.PLAN_FREE_TRIAL];

interface Props {
  title: string;
  created: string | null;
  file: FileData | null;
  noteGenerationBlocked: boolean;
  surface: AssistantSurface;
  onEditTitleClick: () => void;
  onGenerateReferralLetter: () => void;
  onGeneratePatientSummary: (
    readingLevel: ReadingLevel,
    language: SummaryLanguage,
  ) => void;
  // Visit sources a new note includes (assistant upload).
  sourceIds?: readonly number[];
  // v3.2: the one "Copy to chart" for the clinical note (through the
  // "[verify]" review), the primary action; Create becomes secondary.
  copyToChart?: { onCopy: () => void; disabled: boolean };
}

// One "Create" menu for the visit's documents: a new note (opens the template
// picker), a referral letter or a patient summary (after choosing its reading
// level). While the recording usage check fails, Create is disabled and its
// tooltip links to Settings. Free plans can create referral letters and
// patient summaries (within their recording minutes); a new note stays a
// paid feature.
export const CreateMenu = ({
  disabled,
  noteGenerationBlocked,
  onNewNote,
  onGenerateReferralLetter,
  onGeneratePatientSummary,
  onFillForm,
  secondary = false,
  appearance = "classic",
}: {
  disabled: boolean;
  noteGenerationBlocked: boolean;
  onNewNote: () => void;
  onGenerateReferralLetter: () => void;
  onGeneratePatientSummary: (
    readingLevel: ReadingLevel,
    language: SummaryLanguage,
  ) => void;
  // "Fill a form" (VITE_FORM_COMPLETION_ENABLED).
  onFillForm?: () => void;
  // Another action is the primary one (Copy to chart).
  secondary?: boolean;
  // v3.2 header: a quiet "Create" (icon only on phones).
  appearance?: "classic" | "v3";
}): JSX.Element => {
  const { t } = useTranslation();
  const { failed, pending } = useSubscriptionValidation(NOTE_VALIDATIONS);
  const plan = useSubscriptionValidation(PLAN_VALIDATIONS);
  const [levelOpen, setLevelOpen] = useState(false);
  const v3 = appearance === "v3";
  const triggerClass = v3
    ? cn(v3ButtonVariants({ variant: "ghost" }), "max-sm:w-11 max-sm:px-0")
    : "h-11 gap-1.5 px-4 lg:h-9";
  const label = v3 ? (
    <>
      <Plus aria-hidden />
      <span className="max-sm:sr-only">{t("visit.workspace.create")}</span>
      <ChevronDown aria-hidden className="opacity-60 max-sm:hidden" />
    </>
  ) : (
    <>
      <Plus aria-hidden />
      {t("visit.workspace.create")}
      <ChevronDown aria-hidden className="opacity-80" />
    </>
  );

  if (disabled || failed || pending) {
    return (
      <SubscriptionOnlyButton
        className={triggerClass}
        disabled={disabled}
        validations={NOTE_VALIDATIONS}
        variant={v3 ? "ghost" : secondary ? "outline" : undefined}
      >
        {label}
      </SubscriptionOnlyButton>
    );
  }

  const itemClass = v3
    ? V3_MENU_ITEM
    : "min-h-11 gap-3 px-3 text-sm lg:min-h-9";
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {v3 ? (
            <button
              className={triggerClass}
              data-testid="create-menu"
              type="button"
            >
              {label}
            </button>
          ) : (
            <Button
              className={triggerClass}
              data-testid="create-menu"
              type="button"
              variant={secondary ? "outline" : undefined}
            >
              {label}
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={v3 ? "end" : "start"}
          className={v3 ? cn(V3_MENU_CONTENT, "min-w-60") : "min-w-56"}
        >
          <DropdownMenuItem
            className={itemClass}
            disabled={!!plan.failed}
            onSelect={onNewNote}
          >
            <FilePlus2 aria-hidden />
            <span className="flex flex-col">
              {t("visit.workspace.createNewNote")}
              {plan.failed && (
                <span className="text-xs text-muted-foreground">
                  {t("visit.workspace.newNotePaidOnly")}
                </span>
              )}
            </span>
          </DropdownMenuItem>
          <DropdownMenuItem
            className={itemClass}
            disabled={noteGenerationBlocked}
            onSelect={onGenerateReferralLetter}
          >
            <Mail aria-hidden />
            {t("visit.workspace.createReferralLetter")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className={itemClass}
            disabled={noteGenerationBlocked}
            onSelect={() => {
              setLevelOpen(true);
            }}
          >
            <UserRound aria-hidden />
            {t("visit.workspace.createPatientSummary")}
          </DropdownMenuItem>
          {onFillForm && (
            <DropdownMenuItem
              className={itemClass}
              data-testid="create-fill-form"
              onSelect={onFillForm}
            >
              <ClipboardPen aria-hidden />
              {t("forms.createItem")}
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <NoteLanguageCaption />
        </DropdownMenuContent>
      </DropdownMenu>
      <ReadingLevelDialog
        mode="create"
        open={levelOpen}
        onConfirm={onGeneratePatientSummary}
        onOpenChange={setLevelOpen}
      />
    </>
  );
};

// v3.2 header: the Create menu (quiet) with the new-note picker it opens.
export const WorkspaceCreate = ({
  file,
  noteGenerationBlocked,
  sourceIds,
  onGenerateReferralLetter,
  onGeneratePatientSummary,
}: {
  file: FileData | null;
  noteGenerationBlocked: boolean;
  sourceIds?: readonly number[];
  onGenerateReferralLetter: () => void;
  onGeneratePatientSummary: (
    readingLevel: ReadingLevel,
    language: SummaryLanguage,
  ) => void;
}): JSX.Element => {
  const [isNewNoteOpen, setIsNewNoteOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  // AI form completion: a form filled from this visit (web only for v1).
  const forms = AppConfig.enableFormCompletion && !!file?.public_id;
  return (
    <>
      <CreateMenu
        appearance="v3"
        disabled={!file}
        noteGenerationBlocked={noteGenerationBlocked}
        secondary
        onFillForm={forms ? () => setFormOpen(true) : undefined}
        onGeneratePatientSummary={onGeneratePatientSummary}
        onGenerateReferralLetter={onGenerateReferralLetter}
        onNewNote={() => {
          setIsNewNoteOpen(true);
        }}
      />
      <NewNoteSidebar
        open={isNewNoteOpen}
        sourceIds={sourceIds}
        onOpenChange={setIsNewNoteOpen}
      />
      {forms && file && (
        <FormCompletionSheet
          open={formOpen}
          sourceIds={sourceIds ?? []}
          visitPublicId={file.public_id}
          onOpenChange={setFormOpen}
        />
      )}
    </>
  );
};

// Visit title, date and the visit-level actions. Builds on ROSC-45: the
// actions sit on their own row under the title instead of competing with it,
// with one primary action, the Create menu. "Add recording" moved to the
// transcript bar.
export const WorkspaceHeader = ({
  title,
  created,
  file,
  noteGenerationBlocked,
  surface,
  onEditTitleClick,
  onGenerateReferralLetter,
  onGeneratePatientSummary,
  sourceIds,
  copyToChart,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [isNewNoteOpen, setIsNewNoteOpen] = useState(false);
  const isPanel = surface.placement === "panel";
  // The panel toggle only matters while it is collapsed or on touch layouts;
  // on desktop with the panel open the panel's own control hides it.
  const showAssistantToggle = !isPanel || !surface.open;

  return (
    <header className="space-y-3">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-1">
            <h1
              className="line-clamp-2 min-w-0 wrap-break-word text-[22px] font-bold leading-7 sm:text-[28px] sm:leading-[34px]"
              title={title}
            >
              {title}
            </h1>
            <Button
              aria-label={t("visit.updateTitle")}
              className="size-11 shrink-0 text-muted-foreground lg:size-9"
              disabled={!file}
              size="icon"
              type="button"
              variant="ghost"
              onClick={onEditTitleClick}
            >
              <Pencil />
            </Button>
          </div>
          {created && (
            <p className="text-sm text-muted-foreground">
              {t("visit.created")}: {created}
            </p>
          )}
        </div>
        {copyToChart && (
          <Button
            aria-keyshortcuts="Shift+Meta+C Shift+Control+C"
            aria-label={t("visit.workspace.copyToChart")}
            className="h-11 shrink-0 gap-1.5 px-4 lg:h-9"
            data-testid="copy-to-chart"
            disabled={copyToChart.disabled}
            title={t("visit.workspace.copyToChartTooltip")}
            type="button"
            onClick={copyToChart.onCopy}
          >
            <Copy aria-hidden />
            <span aria-hidden className="max-sm:hidden">
              {t("visit.workspace.copyToChart")}
            </span>
            <span aria-hidden className="sm:hidden">
              {t("visit.workspace.copyShort")}
            </span>
          </Button>
        )}
        {showAssistantToggle && (
          <Button
            aria-expanded={surface.open}
            aria-label={t("visit.workspace.showAssistant")}
            className={cn("size-11 shrink-0 sm:h-11 sm:w-auto sm:px-3 lg:h-9")}
            disabled={!file}
            type="button"
            variant="outline"
            onClick={surface.toggle}
          >
            <Sparkles className="text-primary" />
            <span className="max-sm:sr-only">
              {t("visit.workspace.assistant")}
            </span>
          </Button>
        )}
      </div>

      <div
        className="flex flex-wrap items-center gap-2"
        data-testid="visit-actions"
      >
        <CreateMenu
          disabled={!file}
          noteGenerationBlocked={noteGenerationBlocked}
          secondary={!!copyToChart}
          onGeneratePatientSummary={onGeneratePatientSummary}
          onGenerateReferralLetter={onGenerateReferralLetter}
          onNewNote={() => {
            setIsNewNoteOpen(true);
          }}
        />
        <NoteLanguageMenu disabled={!file} />
      </div>
      <NewNoteSidebar
        open={isNewNoteOpen}
        sourceIds={sourceIds}
        onOpenChange={setIsNewNoteOpen}
      />
    </header>
  );
};
